export const DIFFICULTY_IDS = ["jinak", "nakal", "liar", "rakus", "buang_tebiat"] as const;

export type DifficultyId = (typeof DIFFICULTY_IDS)[number];

export interface DifficultyPreset {
  readonly id: DifficultyId;
  readonly label: "Jinak" | "Nakal" | "Liar" | "Rakus" | "Buang Tebiat";
  readonly description: string;
  readonly movementSpeedMultiplier: number;
  readonly lateralSteeringStrength: number;
  readonly lateralSteeringIntervalSeconds: number;
  readonly ghostHealth: Readonly<Record<"toyol" | "polong" | "pocong" | "penanggal" | "pontianak", number>>;
  readonly attackDamageMultiplier: number;
  readonly attackCooldownMultiplier: number;
  readonly captureDurationSeconds: number;
  readonly aggressionDurationMultiplier: number;
  readonly hintIntervalMultiplier: number;
}

export const DEFAULT_DIFFICULTY_ID: DifficultyId = "liar";

export const DIFFICULTY_PRESETS: Readonly<Record<DifficultyId, DifficultyPreset>> = {
  jinak: {
    id: "jinak", label: "Jinak", description: "Hantu kurang agresif.\nSesuai untuk pemain baru.",
    movementSpeedMultiplier: .8, lateralSteeringStrength: .05, lateralSteeringIntervalSeconds: 1.5,
    ghostHealth: { toyol:2, polong:3, pocong:3, penanggal:4, pontianak:4 },
    attackDamageMultiplier: .6, attackCooldownMultiplier: 1.5,
    captureDurationSeconds: 7, aggressionDurationMultiplier: .75, hintIntervalMultiplier: .7,
  },
  nakal: {
    id: "nakal", label: "Nakal", description: "Hantu sedikit nakal.\nCabaran santai.",
    movementSpeedMultiplier: .9, lateralSteeringStrength: .1, lateralSteeringIntervalSeconds: 1.25,
    ghostHealth: { toyol:3, polong:3, pocong:4, penanggal:4, pontianak:5 },
    attackDamageMultiplier: .8, attackCooldownMultiplier: 1.25,
    captureDurationSeconds: 6, aggressionDurationMultiplier: .9, hintIntervalMultiplier: .85,
  },
  liar: {
    id: "liar", label: "Liar", description: "Cabaran seimbang.\nPengalaman asal permainan.",
    movementSpeedMultiplier: 1, lateralSteeringStrength: .16, lateralSteeringIntervalSeconds: 1.05,
    ghostHealth: { toyol:3, polong:3, pocong:4, penanggal:4, pontianak:5 },
    attackDamageMultiplier: 1, attackCooldownMultiplier: 1,
    captureDurationSeconds: 5, aggressionDurationMultiplier: 1, hintIntervalMultiplier: 1,
  },
  rakus: {
    id: "rakus", label: "Rakus", description: "Hantu lebih ganas.\nUntuk pemain berpengalaman.",
    movementSpeedMultiplier: 1.35, lateralSteeringStrength: .24, lateralSteeringIntervalSeconds: .85,
    ghostHealth: { toyol:4, polong:5, pocong:6, penanggal:7, pontianak:8 },
    attackDamageMultiplier: 1.5, attackCooldownMultiplier: .8,
    captureDurationSeconds: 4, aggressionDurationMultiplier: 1.15, hintIntervalMultiplier: 1.25,
  },
  buang_tebiat: {
    id: "buang_tebiat", label: "Buang Tebiat", description: "Hantu sangat agresif.\nCabaran paling ekstrem.",
    movementSpeedMultiplier: 1.7, lateralSteeringStrength: .32, lateralSteeringIntervalSeconds: .7,
    ghostHealth: { toyol:5, polong:7, pocong:9, penanggal:10, pontianak:12 },
    attackDamageMultiplier: 2, attackCooldownMultiplier: .65,
    captureDurationSeconds: 3, aggressionDurationMultiplier: 1.3, hintIntervalMultiplier: 1.5,
  },
};

export const DIFFICULTY_OPTIONS: readonly DifficultyPreset[] = DIFFICULTY_IDS.map((id) => DIFFICULTY_PRESETS[id]);

export function sanitizeDifficultyId(value: unknown): DifficultyId {
  return typeof value === "string" && (DIFFICULTY_IDS as readonly string[]).includes(value)
    ? value as DifficultyId
    : DEFAULT_DIFFICULTY_ID;
}

export function scaleDifficultyValue(baseValue: number, multiplier: number, minimum = 0, maximum = Number.POSITIVE_INFINITY): number {
  return Math.max(minimum, Math.min(maximum, Math.max(0, baseValue) * multiplier));
}

export function scaleDifficultyDamage(baseDamage: number, multiplier: number): number {
  if (!Number.isFinite(baseDamage) || baseDamage <= 0) return 0;
  return Math.max(1, Math.round(baseDamage * multiplier));
}

/** Owns one validated selection and always scales original, unmodified base values. */
export class DifficultyManager {
  private selectedId: DifficultyId;

  constructor(initialId: unknown = DEFAULT_DIFFICULTY_ID) {
    this.selectedId = sanitizeDifficultyId(initialId);
  }

  get currentId(): DifficultyId { return this.selectedId; }
  get current(): DifficultyPreset { return DIFFICULTY_PRESETS[this.selectedId]; }

  select(value: unknown): DifficultyPreset {
    this.selectedId = sanitizeDifficultyId(value);
    return this.current;
  }

  initializeNewCampaign(value: unknown, resetCampaign: (difficultyId: DifficultyId) => void): DifficultyPreset {
    const nextId = sanitizeDifficultyId(value);
    resetCampaign(nextId);
    this.selectedId = nextId;
    return this.current;
  }

  movementSpeed(baseValue: number): number { return scaleDifficultyValue(baseValue, this.current.movementSpeedMultiplier); }
  attackDamage(baseValue: number): number { return scaleDifficultyDamage(baseValue, this.current.attackDamageMultiplier); }
  attackCooldown(baseValue: number, minimum = 0): number { return scaleDifficultyValue(baseValue, this.current.attackCooldownMultiplier, minimum); }
  aggressionDuration(baseValue: number): number { return scaleDifficultyValue(baseValue, this.current.aggressionDurationMultiplier); }
  hintInterval(baseValue: number): number { return scaleDifficultyValue(baseValue, this.current.hintIntervalMultiplier); }
}
